import { useNuxtApp } from 'nuxt/app'

import { log } from '@common/core-layer/services/logger.service'
import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'

import { NotificationService } from '../services/notification.service'
import { notificationQueryKeys } from '../stores/notification.query-keys'
import type { QueryClient } from '@tanstack/vue-query'

/**
 * Marks notifications read from outside a component/vue-query setup context
 * (push notification click bridge, cold-start boot, plugins).
 *
 * `useNotification().markNotificationAsRead` cannot be reused there because it
 * builds a `useMutation` which requires an active Vue injection context. This
 * composable captures `nuxtApp` at setup time and resolves the shared
 * `$queryClient` lazily, so the returned callbacks stay valid inside async
 * listeners that fire long after setup.
 */
export function useNotificationReadSync() {
  const nuxtApp = useNuxtApp()
  const { captureException } = useSentry()

  function resolveQueryClient(): QueryClient | undefined {
    return (nuxtApp as unknown as { $queryClient?: QueryClient }).$queryClient
  }

  /** Refresh the drawer lists + unread count after a read succeeds. */
  function invalidateNotificationQueries(): void {
    const queryClient = resolveQueryClient()
    if (!queryClient) return
    void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() })
  }

  /**
   * Marks the notification identified by `refId` as read. Fire-and-forget by
   * design — a failed read must never block notification navigation.
   *
   * `refId` is attacker-controllable when it arrives from a cold-start URL, so
   * it is only forwarded as an opaque query param; the backend scopes the read
   * to the authenticated user.
   */
  async function markNotificationRefIdAsRead(refId: unknown): Promise<boolean> {
    const normalizedRefId = typeof refId === 'string' ? refId.trim() : ''
    if (!normalizedRefId) return false

    try {
      await new NotificationService().markNotificationAsRead(undefined, normalizedRefId)
      invalidateNotificationQueries()
      return true
    } catch (err) {
      // Structured log for queryable per-tenant/per-release diagnosis, plus Sentry so the
      // regression surfaces in the error pipeline rather than only in a log sink.
      const readError = err instanceof Error ? err : new Error('Failed to mark notification as read from push click')
      log.error('Failed to mark notification as read from push click', { error: readError })
      captureException(readError)
      return false
    }
  }

  return {
    markNotificationRefIdAsRead,
    invalidateNotificationQueries,
  }
}
